<script lang="ts" setup>
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AgenticAccessPanel from "./AgenticAccessPanel.vue";
import PersistentIPsPanel from "./PersistentIPsPanel.vue";
import { useAuthStore, type ACL } from "../pinia/auth";
import { useThemeStore } from "../pinia/theme";
import { getAclStatus, type AclStatusKey } from "../shared/services/aclStatus";

const props = defineProps<{
  acls: ACL[];
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const themeStore = useThemeStore();

// Active-tab accent follows the user dashboard theme (grape by default), not the admin brand red.
const accentVar = computed(() => `--theme-${themeStore.accent ?? "grape"}${themeStore.highAccessibility ? "-hc" : ""}`);
const activeTabStyle = computed(() => ({ borderColor: `hsl(var(${accentVar.value}))` }));
const activeBadgeStyle = computed(() => ({ backgroundColor: `hsl(var(${accentVar.value}) / 0.15)` }));

type TabKey = "agentic" | "persistent";

const expanded = ref(false);
const activeTab = ref<TabKey>("agentic");

const triggerRef = ref<HTMLButtonElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const mobilePanelTop = ref(0);

// Per-tab summaries reported by the embedded bodies so the tab pills stay in sync.
const agenticSummary = ref({ count: 0, hasPending: false });
const persistentSummary = ref({ count: 0, hasPending: false });

// Capability only: available whenever the user has a knoc with persistent/agentic enabled, grant or not.
const eligibleAcls = computed(() => props.acls.filter((acl) => acl.PersistentAndAgenticEnabled));

const featuresEnabled = computed(() => authStore.user?.PersistentAndAgenticEnabled !== false);
const persistentAvailable = computed(
  () => featuresEnabled.value && Boolean(authStore.user?.AllowPersistentIPs) && eligibleAcls.value.length > 0,
);
const agenticAvailable = computed(
  () => featuresEnabled.value && Boolean(authStore.user?.AllowAgenticAccess) && eligibleAcls.value.length > 0,
);
const available = computed(() => persistentAvailable.value || agenticAvailable.value);

// Trigger status dot: orange if anything is pending, green if anything is active. Derived from the status payload so it works while the popover is closed.
const triggerStatus = computed(() => {
  let hasPending = false;
  let hasActive = false;
  for (const acl of props.acls) {
    for (const d of acl.PersistentIPs ?? []) {
      if (getAclStatus(d.Status as AclStatusKey)?.grantIssued) {
        hasActive = true;
      } else {
        hasPending = true;
      }
    }
  }
  return { hasPending, hasActive };
});

function updateMobilePanelTop() {
  if (!triggerRef.value) return;
  mobilePanelTop.value = triggerRef.value.getBoundingClientRect().bottom + 4;
}

function toggle() {
  expanded.value = !expanded.value;
  if (expanded.value) {
    // Remember the last-selected tab across close/reopen; only fall back to the
    // first available tab (Agentic preferred) when the remembered one is gone.
    const currentAvailable = activeTab.value === "agentic" ? agenticAvailable.value : persistentAvailable.value;
    if (!currentAvailable) {
      activeTab.value = agenticAvailable.value ? "agentic" : "persistent";
    }
    nextTick(updateMobilePanelTop);
  }
}

function close() {
  expanded.value = false;
}

function handleDocumentClick(e: MouseEvent) {
  if (!expanded.value) return;
  const path = e.composedPath();
  if (panelRef.value && path.includes(panelRef.value)) return;
  if (triggerRef.value && path.includes(triggerRef.value)) return;
  close();
}

function handleKeydown(e: KeyboardEvent) {
  if (!expanded.value || e.key !== "Escape") return;
  close();
  triggerRef.value?.focus();
}

watch(expanded, (isOpen) => {
  if (isOpen) {
    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("keydown", handleKeydown);
  } else {
    document.removeEventListener("click", handleDocumentClick);
    document.removeEventListener("keydown", handleKeydown);
  }
});

onMounted(() => {
  window.addEventListener("resize", updateMobilePanelTop);
  window.addEventListener("scroll", updateMobilePanelTop, { passive: true });
});
onUnmounted(() => {
  window.removeEventListener("resize", updateMobilePanelTop);
  window.removeEventListener("scroll", updateMobilePanelTop);
  document.removeEventListener("click", handleDocumentClick);
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
  <div v-if="available" class="relative inline-block">
    <button
      ref="triggerRef"
      type="button"
      :aria-expanded="expanded"
      aria-controls="access-menu-panel"
      :aria-label="t('accessMenu.title')"
      class="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-10 md:w-10 lg:h-11 lg:w-11 [&_svg]:pointer-events-none"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fass', 'circle-nodes']" class="h-4 w-4 md:h-5 md:w-5" />
      <span
        v-if="triggerStatus.hasPending"
        class="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border-2 border-background bg-orange-500"
        role="status"
      >
        <span class="sr-only">{{ t("accessMenu.statusPending") }}</span>
      </span>
      <span
        v-else-if="triggerStatus.hasActive"
        class="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border-2 border-background bg-green-500"
        aria-hidden="true"
      ></span>
    </button>

    <!-- Mobile-only backdrop -->
    <div
      v-if="expanded"
      class="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm sm:hidden"
      aria-hidden="true"
      @click="close"
    ></div>

    <div
      v-if="expanded"
      id="access-menu-panel"
      ref="panelRef"
      :style="{ '--mobile-panel-top': mobilePanelTop + 'px' }"
      class="fixed inset-x-2 top-[var(--mobile-panel-top)] z-50 max-h-[calc(100dvh-var(--mobile-panel-top)-1rem)] overflow-y-auto overscroll-contain rounded-md border border-border bg-background shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:max-h-none sm:w-[28rem] sm:overflow-visible"
    >
      <button
        type="button"
        :aria-label="t('common.close')"
        class="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground sm:hidden"
        @click="close"
      >
        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
      </button>

      <!-- Tabs -->
      <div class="flex gap-1 border-b border-border px-2 pt-2" role="tablist" :aria-label="t('accessMenu.title')">
        <button
          v-if="agenticAvailable"
          type="button"
          role="tab"
          :aria-selected="activeTab === 'agentic'"
          :style="activeTab === 'agentic' ? activeTabStyle : undefined"
          :class="[
            'flex flex-1 items-center justify-center gap-2 border-b-2 px-2 py-2 text-sm font-medium transition-colors',
            activeTab === 'agentic'
              ? 'text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          ]"
          @click="activeTab = 'agentic'"
        >
          <font-awesome-icon :icon="['fass', 'circle-nodes']" class="h-3.5 w-3.5" aria-hidden="true" />
          {{ t("accessMenu.tabs.agentic") }}
          <span
            v-if="agenticSummary.count > 0"
            class="rounded-full px-1.5 text-xs"
            :style="activeTab === 'agentic' ? activeBadgeStyle : undefined"
            :class="activeTab === 'agentic' ? 'text-foreground' : 'bg-muted text-muted-foreground'"
            >{{ agenticSummary.count }}</span
          >
        </button>
        <button
          v-if="persistentAvailable"
          type="button"
          role="tab"
          :aria-selected="activeTab === 'persistent'"
          :style="activeTab === 'persistent' ? activeTabStyle : undefined"
          :class="[
            'flex flex-1 items-center justify-center gap-2 border-b-2 px-2 py-2 text-sm font-medium transition-colors',
            activeTab === 'persistent'
              ? 'text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          ]"
          @click="activeTab = 'persistent'"
        >
          <font-awesome-icon :icon="['fass', 'laptop-mobile']" class="h-3.5 w-3.5" aria-hidden="true" />
          {{ t("accessMenu.tabs.persistent") }}
          <span
            v-if="persistentSummary.count > 0"
            class="rounded-full px-1.5 text-xs"
            :style="activeTab === 'persistent' ? activeBadgeStyle : undefined"
            :class="activeTab === 'persistent' ? 'text-foreground' : 'bg-muted text-muted-foreground'"
            >{{ persistentSummary.count }}</span
          >
        </button>
      </div>

      <div class="p-4">
        <template v-if="agenticAvailable">
          <AgenticAccessPanel v-show="activeTab === 'agentic'" :acls="acls" @summary="agenticSummary = $event" />
        </template>
        <template v-if="persistentAvailable">
          <PersistentIPsPanel v-show="activeTab === 'persistent'" :acls="acls" @summary="persistentSummary = $event" />
        </template>
      </div>
    </div>
  </div>
</template>
